import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from 'react';

import { logger } from '../../../core/Log/logger';
import { useAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { trackCustomEvent } from '../../../core/Tracking/GoogleAnalytics';
import { getUserFromPyrite } from '../../../resources/user/api/pyrite/user';
import { getUserHash } from '../../../resources/user/domain/operations/getUserHash';
import { setUserTrackingIdCookie } from '../../../resources/user/domain/operations/setUserTrackingIdCookie';
import { useLocale } from '../../hooks/useLocale';
import { WishListProvider } from '../wishList/WishListProvider';

export type UserForProvider = Awaited<ReturnType<typeof getUserFromPyrite>>;

export type UserContextValue = {
    user: UserForProvider;
    isUserFetched: boolean;
    isUserConnectedOnLoad: boolean;
    isUserConnected: boolean;
    fetchUser: () => Promise<UserForProvider>;
};

export const UserContext = React.createContext<UserContextValue>({
    user: null,
    isUserFetched: false,
    isUserConnectedOnLoad: false,
    isUserConnected: false,
    fetchUser: () => Promise.resolve(null),
});

type UserProviderProps = {
    isUserConnectedOnLoad: boolean;
    user: UserForProvider;
};

export function UserProvider({
    children,
    isUserConnectedOnLoad,
    user: initialUser,
}: PropsWithChildren<UserProviderProps>) {
    const { setIsConnected, isAmplitudeEnabled } = useAmplitude();
    const [user, setUser] = useState<UserForProvider>(initialUser);
    const [isUserFetched, setIsUserFetched] = useState(false);
    const identifyUserIsConnectedOnMountRef = useRef(isUserConnectedOnLoad);
    const { locale } = useLocale();

    const isUserConnected = isUserFetched && user !== null;

    const setupUser = useCallback(async (user: UserForProvider) => {
        setUser(user);
        setIsUserFetched(true);

        if (user) {
            const { userMD5 } = await getUserHash(user);
            trackCustomEvent({
                event: 'user_connected',
                user_md5: userMD5,
            });
            await setUserTrackingIdCookie(user);
        }
    }, []);

    const fetchUser = useCallback(async (): Promise<UserForProvider> => {
        let user: UserForProvider = null;
        try {
            user = await getUserFromPyrite(locale);
        } catch (error) {
            setIsUserFetched(true);
            throw new Error(`User request failed with error ${String(error)}`);
        }

        await setupUser(user);

        return user;
    }, [locale, setupUser]);

    useEffect(() => {
        if (initialUser !== null) {
            void setupUser(initialUser);
            return;
        }

        if (!isUserConnectedOnLoad) {
            return;
        }

        fetchUser().catch((error) => {
            logger.error(
                'UserProvider user fetch failed on mount',
                { user: initialUser, isUserConnectedOnLoad },
                error,
            );
        });
    }, [fetchUser, initialUser, setupUser, isUserConnectedOnLoad]);

    useEffect(
        function identifyUserIsConnectedOnMount() {
            if (!identifyUserIsConnectedOnMountRef.current) {
                return;
            }

            if (!isAmplitudeEnabled || !isUserFetched) {
                return;
            }

            identifyUserIsConnectedOnMountRef.current = false;

            if (user) {
                setIsConnected(user);
            }
        },
        [isAmplitudeEnabled, isUserFetched, user, setIsConnected],
    );

    return (
        <UserContext.Provider
            value={{
                user,
                isUserFetched,
                fetchUser,
                isUserConnectedOnLoad,
                isUserConnected,
            }}
        >
            <WishListProvider>{children}</WishListProvider>
        </UserContext.Provider>
    );
}
